/**
 * Diagnostics for Evervault card-iframe failures.
 *
 * Evervault's web SDK fires the card `onError` callback with NO argument (it is
 * typed `() => void`), and the card renders inside a cross-origin iframe that
 * PostHog session replay cannot see into. So the `card_iframe_error` event on
 * its own cannot tell us *why* a load failed. These helpers capture ambient
 * context at error time — network / page visibility, resource timing for the
 * Evervault assets, and CSP / capture-phase resource-load breadcrumbs — to
 * distinguish ad-block / CSP blocks, offline / slow network, and genuine
 * Evervault-side failures.
 *
 * Everything here is SSR-safe and must never throw: instrumentation must not
 * break the card form.
 */

const EVERVAULT_HOST_RE = /(^|\.)evervault\.(com|io|app|dev)$/i

export const isEvervaultUrl = (value: unknown): boolean => {
  if (typeof value !== 'string' || value.length === 0) return false
  try {
    const base =
      typeof window !== 'undefined' ? window.location.href : undefined
    return EVERVAULT_HOST_RE.test(new URL(value, base).hostname)
  } catch {
    return value.toLowerCase().includes('evervault')
  }
}

const stripQuery = (url: string): string => {
  const q = url.indexOf('?')
  return q === -1 ? url : url.slice(0, q)
}

export interface EvervaultResourceTiming {
  name: string
  initiatorType: string
  startTime: number
  duration: number
  responseEnd: number
  nextHopProtocol: string
  /**
   * Whether the entry exposes cross-origin-restricted timing detail. Without a
   * `Timing-Allow-Origin` response header from Evervault, the spec zeroes
   * `responseStart`, `nextHopProtocol`, `responseStatus` and the size fields —
   * but `startTime`, `responseEnd` and `duration` stay accurate either way, so
   * `duration` remains a safe cross-origin signal. `tao` records which regime
   * the entry is in, so a zeroed `responseStart`/`nextHopProtocol` is never
   * misread as a failed load.
   */
  tao: boolean
}

export const collectEvervaultResourceTiming = (
  limit = 8
): EvervaultResourceTiming[] => {
  try {
    if (
      typeof performance === 'undefined' ||
      typeof performance.getEntriesByType !== 'function'
    ) {
      return []
    }
    const entries = performance.getEntriesByType(
      'resource'
    ) as PerformanceResourceTiming[]
    return entries
      .filter(entry => isEvervaultUrl(entry.name))
      .slice(-limit)
      .map(entry => ({
        name: stripQuery(entry.name),
        initiatorType: entry.initiatorType,
        startTime: Math.round(entry.startTime),
        duration: Math.round(entry.duration),
        responseEnd: Math.round(entry.responseEnd),
        nextHopProtocol: entry.nextHopProtocol,
        tao: entry.responseStart > 0 || entry.nextHopProtocol !== ''
      }))
  } catch {
    return []
  }
}

export interface IframeErrorContextInput {
  mountTime: number
  readyAt: number | null
  cardChanged: boolean
}

export const collectIframeErrorContext = ({
  mountTime,
  readyAt,
  cardChanged
}: IframeErrorContextInput): Record<string, unknown> => {
  const ctx: Record<string, unknown> = {
    everReady: readyAt !== null,
    phase: readyAt !== null ? 'post_ready' : 'pre_ready',
    cardChanged
  }

  try {
    if (
      typeof performance !== 'undefined' &&
      typeof performance.now === 'function'
    ) {
      const now = performance.now()
      ctx.msSinceMount = Math.round(now - mountTime)
      ctx.msSinceReady = readyAt !== null ? Math.round(now - readyAt) : null
    }
  } catch {
    /* noop — diagnostics must never throw */
  }

  try {
    if (typeof navigator !== 'undefined') {
      ctx.online = navigator.onLine
      const connection = (
        navigator as Navigator & {
          connection?: {
            effectiveType?: string
            downlink?: number
            rtt?: number
            saveData?: boolean
          }
        }
      ).connection
      if (connection) {
        ctx.connectionEffectiveType = connection.effectiveType
        ctx.connectionDownlink = connection.downlink
        ctx.connectionRtt = connection.rtt
        ctx.connectionSaveData = connection.saveData
      }
    }
  } catch {
    /* noop */
  }

  try {
    if (typeof document !== 'undefined') {
      ctx.visibilityState = document.visibilityState
      ctx.hasFocus =
        typeof document.hasFocus === 'function'
          ? document.hasFocus()
          : undefined
    }
  } catch {
    /* noop */
  }

  ctx.evervaultResources = collectEvervaultResourceTiming()

  return ctx
}

export const serializeErrorArg = (err: unknown): Record<string, unknown> => {
  if (err === undefined) return { present: false }
  if (err === null) return { present: true, value: null }
  if (err instanceof Error) {
    return {
      present: true,
      name: err.name,
      message: err.message,
      code: (err as Error & { code?: unknown }).code
    }
  }
  if (typeof err === 'object') {
    try {
      return { present: true, value: JSON.parse(JSON.stringify(err)) }
    } catch {
      return { present: true, value: String(err) }
    }
  }
  return { present: true, value: String(err) }
}

export type EvervaultBreadcrumbType =
  | 'csp_violation'
  | 'resource_error'
  | 'unhandled_rejection'

export interface EvervaultBreadcrumb {
  type: EvervaultBreadcrumbType
  at: number
  detail: Record<string, unknown>
}

export interface EvervaultBreadcrumbRecorder {
  getLatest: () => EvervaultBreadcrumb | null
  dispose: () => void
}

const NOOP_RECORDER: EvervaultBreadcrumbRecorder = {
  getLatest: () => null,
  dispose: () => {
    /* noop */
  }
}

const nowMs = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? Math.round(performance.now())
    : Date.now()

/**
 * Installs listeners that record the most recent Evervault-related CSP
 * violation, capture-phase resource-load error, or unhandled rejection. These
 * are the reliable ad-block / CSP signal (resource timing is only
 * corroboration). Call `dispose()` on unmount. Returns a no-op recorder during
 * SSR.
 */
export const createEvervaultErrorBreadcrumbs =
  (): EvervaultBreadcrumbRecorder => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return NOOP_RECORDER
    }

    let latest: EvervaultBreadcrumb | null = null

    const onCsp = (event: SecurityPolicyViolationEvent) => {
      if (
        !isEvervaultUrl(event.blockedURI) &&
        !isEvervaultUrl(event.sourceFile)
      ) {
        return
      }
      latest = {
        type: 'csp_violation',
        at: nowMs(),
        detail: {
          blockedURI: event.blockedURI,
          violatedDirective: event.violatedDirective,
          effectiveDirective: event.effectiveDirective,
          disposition: event.disposition
        }
      }
    }

    // Resource-load errors (<script> / <iframe>) do not bubble, so listen in
    // the capture phase and read the failing element off `event.target`.
    const onError = (event: ErrorEvent) => {
      const target = event.target as
        | (HTMLElement & { src?: string; href?: string })
        | null
      if (!target) return
      const url = target.src ?? target.href
      if (!isEvervaultUrl(url)) return
      latest = {
        type: 'resource_error',
        at: nowMs(),
        detail: { url, tagName: target.tagName }
      }
    }

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason
      const message =
        reason instanceof Error ? reason.message : String(reason ?? '')
      if (!message.toLowerCase().includes('evervault')) return
      latest = {
        type: 'unhandled_rejection',
        at: nowMs(),
        detail: { message }
      }
    }

    document.addEventListener('securitypolicyviolation', onCsp)
    window.addEventListener('error', onError, true)
    window.addEventListener('unhandledrejection', onRejection)

    return {
      getLatest: () => latest,
      dispose: () => {
        document.removeEventListener('securitypolicyviolation', onCsp)
        window.removeEventListener('error', onError, true)
        window.removeEventListener('unhandledrejection', onRejection)
      }
    }
  }
